import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Rubric, RubricLevel, RubricLevelCreatePayload, RubricLevelUpdatePayload, Skill } from '@mira-avant/mtlibs';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import { FormTitleComponent } from '../../../ui/forms/form-title/form-title.component';
import { TextFieldComponent } from '../../../fields/standard/text-field/text-field.component';
import { NumberFieldComponent } from '../../../fields/standard/number-field/number-field.component';
import { TextareaFieldComponent } from '../../../fields/standard/textarea-field/textarea-field.component';
import { RubricDropdownFieldComponent } from '../../../fields/standard/rubric-dropdown-field/rubric-dropdown-field.component';
import { Subscription } from 'rxjs';
import { SkillService } from '../../../../services/skill.service';

@Component({
	selector: 'app-rubric-level-form',
	standalone: true,
	imports: [
		AppModule,
		ZorroModule,
		ThirdPartyModule,
		AngularModule,
		FormTitleComponent,
		TextFieldComponent,
		NumberFieldComponent,
		TextareaFieldComponent,
		RubricDropdownFieldComponent,
	],
	templateUrl: './rubric-level-form.component.html',
	styleUrl: './rubric-level-form.component.scss',
})
export class RubricLevelFormComponent implements OnInit, OnChanges, OnDestroy {
	@Input('rubricLevel') public rubricLevel: RubricLevel | undefined | null;
	@Input('rubric') public rubric: Rubric | undefined | null;
	@Output('onSubmit') public onSubmitEmitter: EventEmitter<RubricLevelCreatePayload | RubricLevelUpdatePayload> =
		new EventEmitter<RubricLevelCreatePayload | RubricLevelUpdatePayload>();

	public form: FormGroup = this.fb.group({
		rubric_code: ['', [Validators.required, Validators.maxLength(255)]],
		level: [1, [Validators.required, Validators.min(0)]],
		name: ['', [Validators.required, Validators.maxLength(50)]],
		short_description: ['', [Validators.required, Validators.maxLength(255)]],
		long_description: ['', [Validators.required]],
		prompt_description: ['', [Validators.required, Validators.pattern(/\S/)]],
		reading_description: [''],
		reading_prompt_description: ['', [Validators.required, Validators.pattern(/\S/)]],
		writing_description: [''],
		writing_prompt_description: ['', [Validators.required, Validators.pattern(/\S/)]],
		listening_description: [''],
		listening_prompt_description: ['', [Validators.required, Validators.pattern(/\S/)]],
		speaking_description: [''],
		speaking_prompt_description: ['', [Validators.required, Validators.pattern(/\S/)]],
	});
	public skills: Skill[] = [];
	private subscriptions: Subscription = new Subscription();

	constructor(
		private fb: FormBuilder,
		private skillService: SkillService
	) {}

	ngOnInit(): void {
		// Subscribe to skills
		this.subscriptions.add(
			this.skillService.skills$.subscribe(skills => {
				this.skills = skills;
			})
		);
		// Fetch skills
		this.skillService.getSkills$().catch(error => {
			console.error('Error fetching skills:', error);
			this.skills = [];
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['rubricLevel'] && this.rubricLevel) {
			const skillDescriptions: { [key: string]: string } = {
				reading_description: '',
				reading_prompt_description: '',
				writing_description: '',
				writing_prompt_description: '',
				listening_description: '',
				listening_prompt_description: '',
				speaking_description: '',
				speaking_prompt_description: '',
			};
			if (this.rubricLevel.rubric_level_skill) {
				this.rubricLevel.rubric_level_skill.forEach(skill => {
					const fieldName = `${skill.skill_code}_description`;
					skillDescriptions[fieldName] = skill.long_description || '';
					skillDescriptions[`${skill.skill_code}_prompt_description`] = skill.prompt_description;
				});
			}
			this.form.patchValue({
				rubric_code: this.rubricLevel.rubric_code,
				level: this.rubricLevel.level,
				name: this.rubricLevel.name,
				short_description: this.rubricLevel.short_description,
				long_description: this.rubricLevel.long_description,
				prompt_description: this.rubricLevel.prompt_description,
				...skillDescriptions,
			});
		} else if (changes['rubric'] && this.rubric) {
			this.form.patchValue({
				rubric_code: this.rubric.code,
			});
		}
	}

	public async onSubmit(): Promise<void> {
		if (this.form.invalid) {
			console.error('Form is invalid', this.form.errors);
			return;
		}

		const payload: RubricLevelCreatePayload | RubricLevelUpdatePayload = {
			...this.form.value,
			rubric_level_skills: this.skills.map(skill => ({
				skill_code: skill.code,
				long_description: this.form.get(`${skill.code}_description`)?.value || '',
				prompt_description: this.form.get(`${skill.code}_prompt_description`)?.value || '',
			})),
		};

		try {
			this.onSubmitEmitter.emit(payload);
			this.clear();
		} catch (error) {
			console.error('Error submitting rubric level:', error);
		}
	}

	public clear(): void {
		this.form.reset({
			level: 1,
			rubric_code: this.rubric?.code || '',
			reading_description: '',
			reading_prompt_description: '',
			writing_description: '',
			writing_prompt_description: '',
			listening_description: '',
			listening_prompt_description: '',
			speaking_description: '',
			speaking_prompt_description: '',
		});
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
